import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';

import { TenantContextService } from './tenant-context.service';

/**
 * Gate a route on the current tenant being the platform-admin root tenant
 * (e.g. `root.brijjsports.com`). Implemented as `CanMatchFn` so the route
 * does not match for non-root tenants — its lazy-loaded bundle is therefore
 * never even fetched. Customers hitting `/admin/**` URLs are bounced to
 * `/not-available`.
 */
export const requireRoot: CanMatchFn = () => {
  const tenants = inject(TenantContextService);
  const router = inject(Router);
  return tenants.isRoot() ? true : router.parseUrl('/not-available');
};

/**
 * Gate a route to non-root (customer/org) tenants only. Root sessions
 * accidentally hitting customer URLs land back at the admin console.
 */
export const requireCustomer: CanMatchFn = () => {
  const tenants = inject(TenantContextService);
  const router = inject(Router);
  return tenants.isCustomer() ? true : router.parseUrl('/admin');
};

/**
 * Block all role routes (`/owner`, `/coach`, `/parent`, `/athlete`) when the
 * tenant's `status === 'suspended'`. Implemented as `CanActivateFn` (not
 * `CanMatchFn`) so suspension can flip at runtime without forcing a bundle
 * reload. Suspended tenants land on `/suspended`.
 */
export const tenantNotSuspended: CanActivateFn = () => {
  const tenants = inject(TenantContextService);
  const router = inject(Router);
  return tenants.currentTenant().status !== 'suspended' ? true : router.parseUrl('/suspended');
};
